// 조용한 온천: 장면을 프로토타입 순서대로 만들고(장식 합치기·장애물 순서가 결과에 영향), 보행 격자를 만든다.
// createQuietRoom이 규칙·카피바라·료칸 식당까지 묶는다.
import * as THREE from 'three';
import { bakeByMaterial } from '../../engine/geometry';
import type { NavGrid } from '../../engine/nav';
import { makeLayout, type QuietLayout } from './layout';
import { buildQuietGrid } from './grid';
import { buildBaths, tickBaths, type Baths } from './baths';
import { buildStreams } from './streams';
import { LANTERN_K, buildBuildings } from './buildings';
import { buildPaths, buildSigns } from './paths';
import { buildBamboo, buildSky, buildSnow, buildTrees, tickSnow, type Snow } from './nature';
import type { QuietScene } from './scene';
import type { FollowCamera } from '../../engine/camera';
import { pose } from '../../capy/pose';
import { tickPops } from '../../food/eating';
import { populate, quietLanding, quietStep, think, type QuietCapy, type QuietRules } from './bots';
import { buildRyokan, dinerThink, doorCheck, type Ryokan } from './ryokan';

export interface QuietWorld {
  /** 방 루트. 방을 바꿀 때 보이기·숨기기. */
  world: THREE.Group;
  /** 하늘·후지산·눈·등불 (scene에 직접 붙어 있음). */
  outside: THREE.Object3D[];
  L: QuietLayout;
  nav: NavGrid;
  baths: Baths;
  snow: Snow;
  lantern: THREE.PointLight;
  /** 매 프레임. (fx, fz): 카메라가 바라보는 곳 (눈이 따라다닌다). */
  tick(t: number, dt: number, capys: readonly { pos: THREE.Vector3; bathAmt: number }[], fx: number, fz: number): void;
}

export function buildQuiet(scene: THREE.Object3D): QuietWorld {
  const L = makeLayout(), world = new THREE.Group(), scenery = new THREE.Group();
  scene.add(world); world.add(scenery);
  const baths = buildBaths(world, scenery, L);
  const Q: QuietScene = { world, scenery, scene, L, steam: baths.steam, paths: [], pathPts: [], creekPts: [] };
  buildStreams(Q);
  const lantern = buildBuildings(Q);
  buildPaths(Q);
  buildBamboo(Q);
  buildSigns(Q);
  buildTrees(Q);
  const snow = buildSnow(scene);
  const { sky, fuji } = buildSky(scene);
  bakeByMaterial(scenery, world);
  const nav = buildQuietGrid(L);
  return {
    world, outside: [sky, fuji, snow.points, lantern], L, nav, baths, snow, lantern,
    tick(t, dt, capys, fx, fz) {
      tickBaths(baths, t, dt, capys);
      lantern.intensity = LANTERN_K * (0.75 + 0.06 * Math.sin(t * 1.3) + 0.03 * Math.sin(t * 3.1));
      tickSnow(snow, t, dt, fx, fz);
    },
  };
}

/** 조용한 온천 방 전체: 장면 + 규칙 + 카피바라 30마리 + 료칸 식당. */
export interface QuietRoom extends QuietWorld {
  R: QuietRules;
  ryokan: Ryokan;
  player: QuietCapy;
  /** 매 프레임: NPC 판단, 이동, 자세, 장면 효과, 접시 애니메이션, 문 확인. */
  update(t: number, dt: number, cam: FollowCamera): void;
}

export function createQuietRoom(scene: THREE.Object3D): QuietRoom {
  const qw = buildQuiet(scene);
  const R: QuietRules = { L: qw.L, nav: qw.nav, agents: [], landing: quietLanding(qw.L) };
  const player = populate(R, qw.world);
  const ryokan = buildRyokan(qw.world, R, qw.snow.points);
  const ctx = { room: 'quiet' as const };
  return {
    ...qw, R, ryokan, player,
    update(t, dt, cam) {
      for (const a of R.agents) { think(R, a, t); pose(a, t, dt, quietStep(R, a, t, dt), ctx); }
      dinerThink(ryokan, qw.world, t);
      qw.tick(t, dt, R.agents, cam.target.x, cam.target.z);
      tickPops(t);
      doorCheck(R, ryokan, player, cam, t);
    },
  };
}
